package com.example.mystybag.appCore.logic.pages

import com.example.mystybag.appCore.model.pages.ReservationDetailsBottomSheetUIState
import com.example.mystybag.appCore.model.pages.ReviewCategoryConfigs
import com.example.mystybag.communication.common.CommonCommands.getBagDetails
import com.example.mystybag.communication.common.CommonCommands.getMerchantLocations
import com.example.mystybag.communication.common.CommonCommands.createReviewCommand
import com.example.mystybag.communication.utils.onResponse
import com.example.mystybag.core.logic.base.AppContext
import com.example.mystybag.core.logic.base.AppViewModel
import com.example.mystybag.core.logic.base.ChildViewModel
import com.example.mystybag.core.model.FullScreenImageOverlay
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_USER_ID
import com.example.mystybag.core.expected.format
import com.example.mystybag.core.expected.Preferences
import com.example.mystybag.core.expected.openLargeMap
import com.example.mystybag.sharedmodels.project.database.UserReservationModel
import com.example.mystybag.sharedmodels.project.requestModels.merchant.GetMerchantLocationsRequestModel
import com.example.mystybag.sharedmodels.project.requestModels.reservations.CreateReviewRequestModel
import com.example.mystybag.sharedmodels.project.responseModels.HomeTopPickUiModel
import com.example.mystybag.sharedmodels.project.responseModels.merchant.GetBagDetailsResponseModel
import com.example.mystybag.sharedmodels.project.responseModels.merchant.GetMerchantLocationsResponseModel
import kotlinx.coroutines.launch

private fun formatReservationAmountFromCatalogBag(m: HomeTopPickUiModel, bagsCount: Int): String? {
    val unit = m.priceWithDiscountNumeric ?: return null
    if (unit <= 0.0) return null
    val currency = Regex("[A-Z]{3}|[€$£¥]").find(m.newPrice)?.value ?: "€"
    return format("%.2f", unit * bagsCount) + " " + currency
}

class ReservationDetailsBottomSheetViewModel(
    context: AppContext,
    reservation: UserReservationModel,
    private val onClose: () -> Unit,
    private val onReservationUpdated: () -> Unit,
    private val onOpenBag: (HomeTopPickUiModel) -> Unit,
    clientCatalogBags: List<HomeTopPickUiModel> = emptyList()
) : ChildViewModel<ReservationDetailsBottomSheetUIState>(
    context,
    run {
        val matched = clientCatalogBags.find { it.id == reservation.bagId.toString() }
        ReservationDetailsBottomSheetUIState(
            reservation = reservation,
            matchedCatalogBag = matched,
            loadedCollectWindowLabel = matched?.collectInfo?.takeIf { it.isNotBlank() },
            loadedAmountFormatted = matched?.let { formatReservationAmountFromCatalogBag(it, reservation.bagsCount) },
            reviewCategories = ReviewCategoryConfigs.getCategoriesForBusinessCategory(reservation.businessCategoryId)
        )
    }
) {

    override fun onCreate() {
        super.onCreate()
        loadLocationAndCollectWindow()
    }

    /** Локация от getMerchantLocations. Детайли за торба само ако няма съвпадение в каталога на Начало (без второ зареждане при съвпадение). */
    private fun loadLocationAndCollectWindow() {
        val r = uiState.value.reservation
        if (r.merchantId == 0L && r.bagId == 0L) return
        val skipBagDetailsApi = uiState.value.matchedCatalogBag != null
        scope.launch {
            if (r.merchantId != 0L) {
                getMerchantLocations(GetMerchantLocationsRequestModel(merchantId = r.merchantId))
                    .onResponse(
                        success = { response ->
                            val locations = (response as? GetMerchantLocationsResponseModel)?.locations ?: emptyList()
                            val loc = locations.find { it.id == r.merchantLocationId } ?: locations.firstOrNull()
                            if (loc != null) {
                                val address = loc.address?.takeIf { it.isNotBlank() }
                                val lat = loc.latitude.toDouble().takeIf { it != -1.0 } ?: 0.0
                                val lon = loc.longitude.toDouble().takeIf { it != -1.0 } ?: 0.0
                                updateState {
                                    it.copy(
                                        loadedPickupAddress = address,
                                        loadedLocationLatitude = lat,
                                        loadedLocationLongitude = lon
                                    )
                                }
                            }
                        },
                        error = { }
                    )
            }

            if (!skipBagDetailsApi && r.bagId != 0L) {
                getBagDetails(r.bagId).onResponse(
                    success = { response ->
                        val bag = response as? GetBagDetailsResponseModel ?: return@onResponse
                        val from = bag.pickupTimeFrom?.takeIf { it.isNotBlank() }
                        val to = bag.pickupTimeTo?.takeIf { it.isNotBlank() }
                        val label = when {
                            from != null && to != null -> "$from – $to"
                            from != null -> from
                            to != null -> to
                            else -> null
                        }
                        val priceWithDiscount = bag.priceWithDiscount ?: 0.0
                        val currency = bag.currency?.takeIf { it.isNotBlank() } ?: "€"
                        val totalAmount = priceWithDiscount * r.bagsCount
                        val amountFormatted = format("%.2f", totalAmount) + " " + currency
                        updateState {
                            it.copy(
                                loadedCollectWindowLabel = label,
                                loadedAmountFormatted = amountFormatted,
                                loadedBagDetails = bag
                            )
                        }
                    },
                    error = { }
                )
            }
        }
    }

    fun onCloseClick() {
        if (uiState.value.isReviewSubmitted) {
            onReservationUpdated()
        } else {
            onClose()
        }
    }

    fun onMenuPhotoClick(url: String) {
        val trimmed = url.trim().takeIf { u -> u.isNotEmpty() } ?: return
        updateState { it.copy(fullScreenMenuPhotoUrl = trimmed) }
        // Show image on entire phone screen (overlay at app root, not inside the dialog)
        AppViewModel.fullScreenImageOverlay.value = FullScreenImageOverlay(trimmed, ::onCloseFullScreenImage)
    }

    fun onCloseFullScreenImage() {
        updateState { it.copy(fullScreenMenuPhotoUrl = null) }
        AppViewModel.fullScreenImageOverlay.value = null
    }

    fun onPickupAddressClick() {
        val state = uiState.value
        val address = state.loadedPickupAddress?.takeIf { it.isNotBlank() } ?: return
        val lat = state.loadedLocationLatitude
        val lon = state.loadedLocationLongitude
        if (lat != 0.0 && lon != 0.0) {
            openLargeMap(lat, lon, address)
        } else {
            openLargeMap(0.0, 0.0, address)
        }
    }

    fun onOpenBagClick() {
        val item = uiState.value.matchedCatalogBag ?: return
        onOpenBag(item)
    }

    
    fun onRatingChanged(categoryKey: String, rating: Int) {
        val state = uiState.value
        val categoryIndex = state.reviewCategories.indexOfFirst { it.key == categoryKey }
        if (categoryIndex == -1) return
        
        updateState {
            when (categoryIndex) {
                0 -> it.copy(ratingCategory1 = rating)
                1 -> it.copy(ratingCategory2 = rating)
                2 -> it.copy(ratingCategory3 = rating)
                3 -> it.copy(ratingCategory4 = rating)
                else -> it
            }
        }
    }
    
    fun onSubmitReviewClick() {
        val state = uiState.value
        if (state.ratingCategory1 == 0 || 
            state.ratingCategory2 == 0 || 
            state.ratingCategory3 == 0 || 
            state.ratingCategory4 == 0) {
            // TODO: Show error message - all ratings must be provided
            return
        }

        submitReview()
    }
    
    private fun submitReview() {
        updateState { it.copy(isSubmittingReview = true, isError = false, errorMessage = "") }
        
        commandBlock(showProgress = false, hideProgress = false) {
            val state = uiState.value
            createReviewCommand(
                CreateReviewRequestModel(
                    userId = Preferences.getLong(PREFERENCES_USER_ID, 0L),
                    reservationId = state.reservation.id,
                    merchantLocationId = state.reservation.merchantLocationId,
                    businessCategoryId = state.reservation.businessCategoryId ?: 0L,
                    ratingCollectionExperience = state.ratingCategory1,
                    ratingFoodQuality = state.ratingCategory2,
                    ratingVarietyOfContents = state.ratingCategory3,
                    ratingFoodQuantity = state.ratingCategory4,
                    couldBeBetterAspects = null
                )
            ).onResponse(
                success = { response ->
                    updateState {
                        it.copy(
                            isSubmittingReview = false,
                            isReviewSubmitted = true,
                            showRatingForm = false
                        )
                    }
                },
                error = { error ->
                    updateState {
                        it.copy(
                            isSubmittingReview = false,
                            isError = true,
                            errorMessage = error.statusMessage ?: "Failed to submit review"
                        )
                    }
                }
            )
        }
    }

}

